import React, { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "./ui/dialog";
import { Play, Pause, Repeat, Sparkles, Volume2 } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const WBW_URL = (s, a, w) =>
  `https://audio.qurancdn.com/wbw/${String(s).padStart(3, "0")}_${String(a).padStart(3, "0")}_${String(w).padStart(3, "0")}.mp3`;

const STATUS_LABEL = {
  wrong: "Mismatch", missed: "Not recited", partial: "Close · revise",
};

export default function PracticeMistakesDrill({ surah, ayah, wordDiff }) {
  const { tx, t } = useLanguage();
  const mistakes = (wordDiff || [])
    .map((w, i) => ({ ...w, idx: i + 1 }))
    .filter((w) => w.status === "wrong" || w.status === "missed" || w.status === "partial");

  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState(0.75);
  const [autoLoop, setAutoLoop] = useState(false);
  const [activeIdx, setActiveIdx] = useState(null);
  const [autoPointer, setAutoPointer] = useState(0);
  const [loopRound, setLoopRound] = useState(0);
  const audioRef = useRef(null);

  // Stop everything when dialog closes
  useEffect(() => {
    if (!open && audioRef.current) {
      audioRef.current.pause();
      setActiveIdx(null);
      setAutoLoop(false);
    }
  }, [open]);

  const playWord = (wordIdx) => {
    if (!audioRef.current) return;
    audioRef.current.src = WBW_URL(surah, ayah, wordIdx);
    audioRef.current.playbackRate = rate;
    setActiveIdx(wordIdx);
    audioRef.current.play().catch(() => setActiveIdx(null));
  };

  const startAutoLoop = () => {
    if (mistakes.length === 0) return;
    setAutoLoop(true);
    setAutoPointer(0);
    setLoopRound(1);
    playWord(mistakes[0].idx);
  };

  const stopAutoLoop = () => {
    setAutoLoop(false);
    audioRef.current?.pause();
    setActiveIdx(null);
  };

  const onEnded = () => {
    if (!autoLoop) { setActiveIdx(null); return; }
    const next = autoPointer + 1;
    if (next < mistakes.length) {
      setAutoPointer(next);
      setTimeout(() => playWord(mistakes[next].idx), 350);
    } else if (loopRound < 3) {
      // Loop 3 times through all mistakes
      setAutoPointer(0);
      setLoopRound(loopRound + 1);
      setTimeout(() => playWord(mistakes[0].idx), 600);
    } else {
      setAutoLoop(false);
      setActiveIdx(null);
    }
  };

  if (mistakes.length === 0) {
    return (
      <div className="text-sm text-forest-700 inline-flex items-center gap-2" data-testid="no-mistakes">
        <Sparkles className="h-4 w-4 text-gold-600" />
        {t("practiceMistakesDrill.mashallahNoMistakesToDrill")}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-accent text-sm" data-testid="open-practice-drill">
          <Repeat className="h-4 w-4" /> {t("practiceMistakesDrill.practiceMistakes", { count: mistakes.length })}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-xl" data-testid="practice-drill-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-forest-700">{t("practiceMistakesDrill.drillTheTrickyBits")}</DialogTitle>
          <DialogDescription className="text-sm text-foreground/70 mt-1">
            {t("practiceMistakesDrill.tapAnyWordToHear")}
          </DialogDescription>
        </DialogHeader>

        <audio ref={audioRef} onEnded={onEnded} onPause={() => !autoLoop && setActiveIdx(null)} data-testid="drill-audio" />

        <div className="mt-2 space-y-4">
          {/* Speed control */}
          <div className="flex items-center gap-3 text-sm">
            <Volume2 className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">{t("practiceMistakesDrill.speed")}</span>
            <div className="flex gap-1.5">
              {[0.5, 0.75, 1].map((r) => (
                <button
                  key={r}
                  onClick={() => { setRate(r); if (audioRef.current) audioRef.current.playbackRate = r; }}
                  data-testid={`drill-rate-${r}`}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                    rate === r ? "bg-forest-600 text-sand-100 border-forest-600" : "bg-white border-sand-300 hover:bg-sand-50"
                  }`}
                >
                  {r}x
                </button>
              ))}
            </div>
            <div className="ml-auto">
              {autoLoop ? (
                <button onClick={stopAutoLoop} className="btn-ghost text-xs" data-testid="stop-auto-loop">
                  <Pause className="h-3.5 w-3.5" /> {t("practiceMistakesDrill.stopAutoLoop")}
                </button>
              ) : (
                <button onClick={startAutoLoop} className="btn-primary text-xs" data-testid="start-auto-loop">
                  <Play className="h-3.5 w-3.5" /> {t("practiceMistakesDrill.autoLoop3")}
                </button>
              )}
            </div>
          </div>

          {autoLoop && (
            <div className="text-xs text-muted-foreground text-center" data-testid="auto-loop-status">
              {t("practiceMistakesDrill.loopWord", { loopRound, word: autoPointer + 1, total: mistakes.length })}
            </div>
          )}

          {/* Words */}
          <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
            {mistakes.map((m) => {
              const isActive = activeIdx === m.idx;
              return (
                <button
                  key={m.idx}
                  onClick={() => playWord(m.idx)}
                  data-testid={`drill-word-${m.idx}`}
                  className={`w-full flex items-center justify-between gap-3 p-3 rounded-xl border transition ${
                    isActive ? "border-forest-600 bg-forest-600/8 ring-2 ring-forest-600/20" : "border-sand-300 bg-white hover:bg-sand-50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`h-9 w-9 rounded-full grid place-items-center text-sm font-display font-semibold ${
                      isActive ? "bg-forest-600 text-sand-100" : "bg-sand-200 text-foreground/70"
                    }`}>
                      {m.idx}
                    </div>
                    <div className="text-left">
                      <div className="font-arabic text-2xl text-forest-700" dir="rtl">{m.word}</div>
                      <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">
                        {tx(STATUS_LABEL[m.status])}
                      </div>
                    </div>
                  </div>
                  <div className={`h-9 w-9 rounded-full grid place-items-center ${
                    isActive ? "bg-forest-600 text-sand-100 animate-pulse-soft" : "bg-sand-200 text-forest-700"
                  }`}>
                    <Play className="h-4 w-4" />
                  </div>
                </button>
              );
            })}
          </div>

          <p className="text-[11px] text-muted-foreground text-center pt-1">
            {t("practiceMistakesDrill.wordAudioViaQuranCom")}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
